import { IAddressWithIdAndIsDefault } from 'src/types/misc';
import { v4 } from 'uuid';

export type AddressKind = 'pickup' | 'drop';

export interface IndexedAddress extends IAddressWithIdAndIsDefault {
    index: number;
}

export interface PassengerAddressChoice {
    pickup: IndexedAddress | null;
    drop: IndexedAddress | null;
}

/** Chosen pickup / drop address per passId. A missing entry or a null side means "server default". */
export type AddressChoices = Record<string, PassengerAddressChoice>;

export interface RawPassengerAddress {
    addressIndex?: string | number;
    addressCode?: string;
    city?: string;
    street?: string;
    houseNum?: string;
    isDefault?: string | number | boolean;
}

export interface OrderGroup {
    passIds: string[];
    addresses: { pickup: IndexedAddress | null; drop: IndexedAddress | null };
}

const EMPTY_CHOICE: PassengerAddressChoice = { pickup: null, drop: null };

/**
 * Maps the passenger's addresses (ws_MyWayStudio_Get_Passengers_Details) to indexed addresses.
 * The index is the address's addressIndex ('0' is a real index — the passenger form saves the main
 * address with it), or its 1-based position when the server sent none. When two addresses share an
 * index, the first one wins.
 */
const toIndexedAddresses = (rawAddresses: RawPassengerAddress[] | null | undefined): IndexedAddress[] => {
    if (!Array.isArray(rawAddresses)) return [];

    const byIndex = new Map<number, IndexedAddress>();

    rawAddresses.forEach((address, position) => {
        const hasServerIndex = address.addressIndex !== undefined && address.addressIndex !== null && address.addressIndex !== '';
        const serverIndex = Number(address.addressIndex);
        const index = hasServerIndex && Number.isInteger(serverIndex) && serverIndex >= 0 ? serverIndex : position + 1;

        if (byIndex.has(index)) return;

        byIndex.set(index, {
            index,
            id: address.addressCode ? String(address.addressCode) : v4(),
            city: address.city || '',
            street: address.street || '',
            houseNum: address.houseNum || '',
            isDefault: String(address.isDefault) === '1' || address.isDefault === true,
        });
    });

    return Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
};

/** All address indexes that at least one of the passengers has, ascending. */
const getIndexColumns = (addressesByPassenger: Record<string, IndexedAddress[]>): number[] => {
    const indexes = new Set<number>();
    Object.values(addressesByPassenger).forEach((addresses) => addresses.forEach((a) => indexes.add(a.index)));
    return Array.from(indexes).sort((a, b) => a - b);
};

const getChoice = (choices: AddressChoices, passId: string): PassengerAddressChoice =>
    choices[passId] || EMPTY_CHOICE;

/**
 * Applies one address index to every passenger, for pickup or for drop.
 * A passenger with no address at that index is left on the server default, and reported as missing.
 */
const applyIndexToAll = (
    passIds: string[],
    addressesByPassenger: Record<string, IndexedAddress[]>,
    choices: AddressChoices,
    kind: AddressKind,
    index: number
): { choices: AddressChoices; missingPassIds: string[] } => {
    const nextChoices: AddressChoices = { ...choices };
    const missingPassIds: string[] = [];

    passIds.forEach((passId) => {
        const match = (addressesByPassenger[passId] || []).find((a) => a.index === index) || null;
        if (!match) missingPassIds.push(passId);
        nextChoices[passId] = { ...getChoice(choices, passId), [kind]: match };
    });

    return { choices: nextChoices, missingPassIds };
};

/**
 * Whether "for all" at this index is what the choices hold now: every passenger with that index has it chosen,
 * and every passenger without it is on the default. A second "for all" click then clears the side for everybody.
 */
const isIndexAppliedToAll = (
    passIds: string[],
    addressesByPassenger: Record<string, IndexedAddress[]>,
    choices: AddressChoices,
    kind: AddressKind,
    index: number
): boolean => {
    let anyHasIndex = false;
    const allMatch = passIds.every((passId) => {
        const hasIndex = (addressesByPassenger[passId] || []).some((a) => a.index === index);
        const chosen = getChoice(choices, passId)[kind];
        if (hasIndex) anyHasIndex = true;
        return hasIndex ? chosen?.index === index : chosen === null;
    });
    return anyHasIndex && allMatch;
};

/** Returns every passenger's pickup or drop to the default */
const clearKindForAll = (passIds: string[], choices: AddressChoices, kind: AddressKind): AddressChoices => {
    const nextChoices: AddressChoices = { ...choices };
    passIds.forEach((passId) => {
        nextChoices[passId] = { ...getChoice(choices, passId), [kind]: null };
    });
    return nextChoices;
};

/** Sets one passenger's pickup or drop address. Choosing the selected address again clears it. */
const togglePassengerChoice = (
    choices: AddressChoices,
    passId: string,
    kind: AddressKind,
    address: IndexedAddress
): AddressChoices => {
    const current = getChoice(choices, passId);
    const isSame = current[kind]?.index === address.index;
    return { ...choices, [passId]: { ...current, [kind]: isSame ? null : address } };
};

const addressKey = (address: IndexedAddress | null) =>
    address ? [address.city, address.street, address.houseNum].join('\u0001') : '';

/**
 * Splits the passengers into groups that share the same pickup and drop address,
 * keeping the order of first appearance. Each group is one ws_MyWayStudio_Set_Passengers_Orders call.
 */
const groupPassengersByAddresses = (passIds: string[], choices: AddressChoices): OrderGroup[] => {
    const groups = new Map<string, OrderGroup>();

    passIds.forEach((passId) => {
        const { pickup, drop } = getChoice(choices, passId);
        const key = `${addressKey(pickup)}|${addressKey(drop)}`;
        const group = groups.get(key);

        if (group) group.passIds.push(passId);
        else groups.set(key, { passIds: [passId], addresses: { pickup, drop } });
    });

    return Array.from(groups.values());
};

/** Keeps only the choices of passengers that are still selected. */
const pruneChoices = (choices: AddressChoices, passIds: string[]): AddressChoices => {
    const next: AddressChoices = {};
    passIds.forEach((passId) => {
        if (choices[passId]) next[passId] = choices[passId];
    });
    return next;
};

/** Runs fn over the items with at most `limit` calls in flight. Results keep the items' order. */
const runWithConcurrency = async <T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> => {
    const results: R[] = new Array(items.length);
    let next = 0;

    const worker = async () => {
        while (next < items.length) {
            const current = next;
            next += 1;
            // eslint-disable-next-line no-await-in-loop
            results[current] = await fn(items[current]);
        }
    };

    await Promise.all(Array.from({ length: Math.min(Math.max(limit, 1), items.length) }, worker));
    return results;
};

/** What happened to one group of passengers; error is null when it was saved */
export interface GroupOutcome {
    passIds: string[];
    error: string | null;
}

/**
 * Sends the groups one after another (never in parallel, so a long selection does not burst the server).
 * A group whose call throws is reported with errorOnThrow, and the next groups are still sent.
 * @param send - resolves to null when the group was saved, or to the error to show
 * @param onProgress - before each group and once at the end: passengers done so far, and the number (1-based)
 * of the last passenger in the group now being sent
 */
const sendGroupsInOrder = async <G extends { passIds: string[] }>(
    groups: G[],
    send: (group: G) => Promise<string | null>,
    errorOnThrow: string,
    onProgress?: (done: number, current: number) => void
): Promise<GroupOutcome[]> => {
    const outcomes: GroupOutcome[] = [];
    let done = 0;

    for (let i = 0; i < groups.length; i += 1) {
        const group = groups[i];
        onProgress?.(done, done + group.passIds.length);
        try {
            // eslint-disable-next-line no-await-in-loop
            outcomes.push({ passIds: group.passIds, error: await send(group) });
        } catch (error) {
            console.error(error);
            outcomes.push({ passIds: group.passIds, error: errorOnThrow });
        }
        done += group.passIds.length;
    }
    onProgress?.(done, done);

    return outcomes;
};

const summarizeOutcomes = (outcomes: GroupOutcome[]) => {
    const failed = outcomes.filter((outcome) => outcome.error !== null);
    const collectIds = (list: GroupOutcome[]) => list.reduce<string[]>((acc, o) => acc.concat(o.passIds), []);

    return {
        allSaved: failed.length === 0,
        failed,
        savedIds: collectIds(outcomes.filter((outcome) => outcome.error === null)),
        failedIds: collectIds(failed),
    };
};

/**
 * Whether the placement form shows the matrix: two or more passengers, or one passenger who still has a
 * choice in it (what is left after a partial save — the single-passenger dropdowns would forget the choice).
 */
const shouldUseMatrix = (selectedPassIds: string[], choices: AddressChoices): boolean => {
    if (selectedPassIds.length > 1) return true;
    if (selectedPassIds.length !== 1) return false;
    const choice = choices[selectedPassIds[0]];
    return !!(choice?.pickup || choice?.drop);
};

/** Identifies one side of an order already saved by this form, so a retry does not order it again */
const savedSideKey = (passId: string, side: AddressKind, time: string, datesKey: string) =>
    [passId, side, time, datesKey].join('|');

/** True when that side of the order was already saved for every passenger of the group */
const isSideSavedForAll = (
    saved: Set<string>,
    passIds: string[],
    side: AddressKind,
    time: string,
    datesKey: string
): boolean => passIds.length > 0 && passIds.every((passId) => saved.has(savedSideKey(passId, side, time, datesKey)));

/** How many of the selected passengers have a chosen pickup or drop address, and how many stay on the default */
const countChosen = (passIds: string[], choices: AddressChoices) => {
    const changed = passIds.filter((passId) => {
        const choice = choices[passId];
        return !!(choice?.pickup || choice?.drop);
    }).length;
    return { changed, defaults: passIds.length - changed };
};

const formatAddress = (address: IndexedAddress) =>
    [address.street && `${address.street}${address.houseNum ? ` ${address.houseNum}` : ''}`, address.city]
        .filter(Boolean)
        .join(', ');

const passengersAddressesMatrixHelpers = {
    toIndexedAddresses,
    getIndexColumns,
    getChoice,
    applyIndexToAll,
    isIndexAppliedToAll,
    clearKindForAll,
    togglePassengerChoice,
    groupPassengersByAddresses,
    pruneChoices,
    runWithConcurrency,
    sendGroupsInOrder,
    summarizeOutcomes,
    shouldUseMatrix,
    savedSideKey,
    isSideSavedForAll,
    countChosen,
    formatAddress,
};

export default passengersAddressesMatrixHelpers;
